import { Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Input, Label } from '@lensora/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'

import { api, ApiError } from '@/lib/api'

interface MonitoringSettings {
  frequency: 'daily' | 'weekly'
  day_of_week: number | null
  hour_of_day: number
  report_email: string | null
  cc_emails: string[]
  is_active: boolean
  next_run_at: string | null
}

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export function EmailReportsCard() {
  const queryClient = useQueryClient()
  const [email, setEmail] = React.useState('')
  const [frequency, setFrequency] = React.useState<'daily' | 'weekly'>('weekly')
  const [dayOfWeek, setDayOfWeek] = React.useState(1)
  const [hourOfDay, setHourOfDay] = React.useState(9)
  const [isActive, setIsActive] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)

  const { data } = useQuery({
    queryKey: ['monitoring-settings'],
    queryFn: () => api.get<{ settings: MonitoringSettings | null }>('/api/monitoring/settings'),
  })

  React.useEffect(() => {
    const s = data?.settings
    if (!s) return
    if (s.report_email) setEmail(s.report_email)
    setFrequency(s.frequency)
    setDayOfWeek(s.day_of_week ?? 1)
    setHourOfDay(s.hour_of_day)
    setIsActive(s.is_active)
  }, [data?.settings])

  const save = useMutation({
    mutationFn: () =>
      api.post('/api/monitoring/settings', {
        report_email: email.trim(),
        frequency,
        day_of_week: frequency === 'weekly' ? dayOfWeek : null,
        hour_of_day: hourOfDay,
        is_active: isActive,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['monitoring-settings'] })
      setError(null)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Failed to save settings.')
    },
  })

  const selectCls =
    'h-9 w-full rounded-xl border border-border bg-surface px-3 text-sm text-text'

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email reports</CardTitle>
        <CardDescription>
          Automated AI visibility reports for your active website, sent on your schedule.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="report-email">Report email</Label>
          <Input
            id="report-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="reports@youragency.com"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="report-frequency">Frequency</Label>
            <select
              id="report-frequency"
              className={selectCls}
              value={frequency}
              onChange={(e) => setFrequency(e.target.value as 'daily' | 'weekly')}
            >
              <option value="weekly">Weekly</option>
              <option value="daily">Daily</option>
            </select>
          </div>
          {frequency === 'weekly' ? (
            <div className="space-y-2">
              <Label htmlFor="report-day">Day</Label>
              <select
                id="report-day"
                className={selectCls}
                value={dayOfWeek}
                onChange={(e) => setDayOfWeek(Number(e.target.value))}
              >
                {DAYS.map((d, i) => (
                  <option key={d} value={i}>{d}</option>
                ))}
              </select>
            </div>
          ) : (
            <div />
          )}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="report-hour">Hour (UTC)</Label>
            <select
              id="report-hour"
              className={selectCls}
              value={hourOfDay}
              onChange={(e) => setHourOfDay(Number(e.target.value))}
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
              ))}
            </select>
          </div>
          <div className="flex items-end pb-1.5">
            <label className="flex items-center gap-2 text-sm text-text">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 rounded border-border"
              />
              Reports enabled
            </label>
          </div>
        </div>
        {error && <p className="text-sm text-feedback-error">{error}</p>}
        {saved && <p className="text-sm text-feedback-success">Report settings saved.</p>}
      </CardContent>
      <CardFooter>
        <Button size="sm" onClick={() => save.mutate()} disabled={!email.trim() || save.isPending}>
          {save.isPending ? 'Saving…' : 'Save report settings'}
        </Button>
      </CardFooter>
    </Card>
  )
}
